import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../utils/constants';
import SuccessModal from '../common/SuccessModal';
import apiService from '../../services/api';

const Header = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load unread notifications count
  useEffect(() => {
    if (user) {
      loadUnreadCount();
      // Refresh count every 30 seconds
      const interval = setInterval(loadUnreadCount, 30000);
      
      // Listen for notifications read event
      const handleNotificationsRead = () => {
        loadUnreadCount();
      };
      window.addEventListener('notificationsRead', handleNotificationsRead);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('notificationsRead', handleNotificationsRead);
      };
    }
  }, [user]);

  const loadUnreadCount = async () => {
    try {
      // Load read notifications from localStorage
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      
      // Generate notifications from real user data (same logic as NotificationsPage)
      const generatedNotifications = [];
      
      try {
        const purchases = await apiService.getUserPurchases();
        purchases.forEach((purchase) => {
          const notifId = `purchase-${purchase.id}`;
          generatedNotifications.push({
            id: notifId,
            read: readNotificationIds.includes(notifId),
            created_at: purchase.purchase_date || purchase.created_at
          });
        });
      } catch (err) {
        console.error('Error loading purchases:', err);
      }

      try {
        const payments = await apiService.getUserManualPayments();
        payments.forEach((payment) => {
          if (payment.status === 'approved' || payment.status === 'rejected') {
            const notifId = `payment-${payment.status}-${payment.id}`;
            generatedNotifications.push({
              id: notifId,
              read: readNotificationIds.includes(notifId),
              created_at: payment.updated_at || payment.created_at
            });
          }
        });
      } catch (err) {
        console.error('Error loading payments:', err);
      }

      const unread = generatedNotifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  const handleLogout = () => {
    logout();
    setShowLogoutSuccess(true);
    setMobileMenuOpen(false);
    setTimeout(() => {
      setShowLogoutSuccess(false);
      navigate(ROUTES.HOME);
    }, 2000);
  };

  return (
    <header className="relative z-50">
      {showLogoutSuccess && (
        <SuccessModal message="تم تسجيل خروجك بنجاح! شكراً لزيارتك" />
      )}
      
      {/* Top Bar - Hidden on mobile */}
      <div className="bg-primary text-white py-2 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex gap-6">
              <a href="mailto:info@allemani.dz" className="flex items-center gap-2 hover:text-accent transition-colors">
                <i className="fas fa-envelope"></i>
                <span>info@allemani.dz</span>
              </a>
            </div>
            <div className="flex gap-4">
              <a href="#" className="hover:text-accent transition-colors"><i className="fab fa-facebook"></i></a>
              <a href="#" className="hover:text-accent transition-colors"><i className="fab fa-instagram"></i></a>
              <a href="#" className="hover:text-accent transition-colors"><i className="fab fa-youtube"></i></a>
              <a href="#" className="hover:text-accent transition-colors"><i className="fab fa-linkedin"></i></a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white shadow-custom">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <Link to={ROUTES.HOME} className="flex items-center">
              <img 
                src="/RIGHTALLEMNI.png" 
                alt="Allemni - علمني" 
                className="h-12 md:h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex gap-8">
              <Link to={ROUTES.HOME} className="text-text2 hover:text-primary font-medium transition-colors">الرئيسية</Link>
              <Link to={ROUTES.COURSES} className="text-text2 hover:text-primary font-medium transition-colors">الدورات</Link>
              <Link to="/storybooks" className="text-text2 hover:text-primary font-medium transition-colors">القصص</Link>
              <Link to="/pricing" className="text-text2 hover:text-primary font-medium transition-colors">الأسعار</Link>
              <Link to={ROUTES.ABOUT} className="text-text2 hover:text-primary font-medium transition-colors">من نحن</Link>
              <Link to={ROUTES.CONTACT} className="text-text2 hover:text-primary font-medium transition-colors">اتصل بنا</Link>
            </nav>

            {/* Desktop Auth Buttons */}
            <div className="hidden lg:flex gap-3 items-center min-w-[400px] justify-end">
              {loading ? (
                // Skeleton loader to prevent layout shift
                <>
                  <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="w-28 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="w-28 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="w-28 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </>
              ) : (
                <>
                  {user && (
                    <Link
                      to="/notifications"
                      className="relative p-2.5 text-text2 hover:text-primary hover:bg-bg2 rounded-lg transition-colors"
                      aria-label="الإشعارات"
                    >
                      <i className="fas fa-bell text-xl"></i>
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-fade-in transition-all duration-300">
                          {unreadCount}
                        </span>
                      )}
                    </Link>
                  )}
                  
                  {user ? (
                    <>
                      <Link
                        to={ROUTES.DASHBOARD}
                        className="px-5 py-2.5 text-primary font-medium hover:bg-bg2 rounded-lg transition-colors whitespace-nowrap"
                      >
                        لوحة التحكم
                      </Link>
                      <Link
                        to="/profile"
                        className="px-5 py-2.5 text-text2 font-medium hover:bg-bg2 rounded-lg transition-colors whitespace-nowrap"
                      >
                        الملف الشخصي
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="px-5 py-2.5 bg-primary text-white font-medium hover:bg-primary-dark rounded-lg transition-colors whitespace-nowrap"
                      >
                        تسجيل الخروج
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to={ROUTES.LOGIN}
                        className="px-5 py-2.5 text-primary font-medium hover:bg-bg2 rounded-lg transition-colors whitespace-nowrap"
                      >
                        تسجيل الدخول
                      </Link>
                      <Link
                        to={ROUTES.REGISTER}
                        className="px-5 py-2.5 bg-primary text-white font-medium hover:bg-primary-dark rounded-lg transition-colors whitespace-nowrap"
                      >
                        إنشاء حساب
                      </Link>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-primary"
            >
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Backdrop */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden fixed inset-x-0 top-[73px] bottom-0 bg-black bg-opacity-50 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-[73px] bg-white border-t z-50 shadow-2xl max-h-[calc(100vh-73px)] overflow-y-auto">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <Link 
                to={ROUTES.HOME} 
                className="text-text2 hover:text-primary font-medium transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                الرئيسية
              </Link>
              <Link 
                to={ROUTES.COURSES} 
                className="text-text2 hover:text-primary font-medium transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                الدورات
              </Link>
              <Link 
                to="/storybooks" 
                className="text-text2 hover:text-primary font-medium transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                القصص المصورة
              </Link>
              <Link 
                to="/pricing" 
                className="text-text2 hover:text-primary font-medium transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                الأسعار
              </Link>
              <Link 
                to={ROUTES.ABOUT} 
                className="text-text2 hover:text-primary font-medium transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                من نحن
              </Link>
              <Link 
                to={ROUTES.CONTACT} 
                className="text-text2 hover:text-primary font-medium transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                اتصل بنا
              </Link>
              
              <div className="border-t pt-4 flex flex-col gap-3">
                {loading ? (
                  // Skeleton loader for mobile
                  <>
                    <div className="h-12 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded-lg animate-pulse"></div>
                  </>
                ) : (
                  <>
                    {user ? (
                      <>
                        <Link
                          to="/notifications"
                          className="px-5 py-2.5 text-center text-text2 font-medium bg-bg2 rounded-lg transition-colors relative"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <i className="fas fa-bell ml-2"></i>
                          الإشعارات
                          {unreadCount > 0 && (
                            <span className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                              {unreadCount}
                            </span>
                          )}
                        </Link>
                        <Link
                          to={ROUTES.DASHBOARD}
                          className="px-5 py-2.5 text-center text-primary font-medium bg-bg2 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <i className="fas fa-home ml-2"></i>
                          لوحة التحكم
                        </Link>
                        <Link
                          to="/profile"
                          className="px-5 py-2.5 text-center text-text2 font-medium bg-bg2 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <i className="fas fa-user ml-2"></i>
                          الملف الشخصي
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="px-5 py-2.5 bg-red-500 text-white font-medium rounded-lg transition-colors hover:bg-red-600"
                        >
                          <i className="fas fa-sign-out-alt ml-2"></i>
                          تسجيل الخروج
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to={ROUTES.LOGIN}
                          className="px-5 py-2.5 text-center text-primary font-medium bg-bg2 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          تسجيل الدخول
                        </Link>
                        <Link
                          to={ROUTES.REGISTER}
                          className="px-5 py-2.5 text-center bg-primary text-white font-medium rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          إنشاء حساب
                        </Link>
                      </>
                    )}
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
